import { ReportListHeader } from '@/modules/move/components/reportListHeader/reportListItemHeader';
import {
  MOVE_SHEET_INITIAL_PAGE,
  MOVE_SHEET_PAGE_SIZE_DEFAUlT,
} from '@/modules/move/entities/constants/moveSheet';
import { MOVE_ROW_PROCESS_STATUS } from '@/modules/move/entities/enums/move';
import {
  MOVE_ERROR_TYPE,
  MoveSheetNormalized,
  MoveSheetParams,
} from '@/modules/move/entities/interfaces/moveSheet';
import { EditedRowByNumber } from '@/modules/move/hooks/useMoveEdition/useMoveEdition';
import useMoveSheet from '@/modules/move/hooks/useMoveSheet/useMoveSheet';
import ICONS from '@/shared/entities/constants/icons';
import { maskText } from '@/utils/format';
import React, { useEffect, useState } from 'react';
import { Accordion, DataTable } from 'vkit/lib/components';
import { Color } from 'vkit/lib/shared/types';

interface ReportReviewListProps {
  moveId?: string;
  linesLoader?: number;
  editedRowByLine?: EditedRowByNumber;
  setRowToEdition?: (moveRow: MoveSheetNormalized) => void;
}

const ReportReviewList: React.FC<ReportReviewListProps> = ({
  moveId,
  linesLoader,
  editedRowByLine,
  setRowToEdition,
}) => {
  const [filters, setFilters] = useState<MoveSheetParams>({});
  const { isLoading, pagination, sheet, showPagination } = useMoveSheet(filters);

  const getButtonLabel = (moveRow: MoveSheetNormalized) => {
    const statusBeneficiario = editedRowByLine?.[String(moveRow.line)]?.fields.statusBeneficiario;

    if (
      editedRowByLine?.[String(moveRow.line)] &&
      statusBeneficiario !== MOVE_ROW_PROCESS_STATUS.WAITING_HEALTH_CARD
    ) {
      return 'Editado';
    }

    if (
      editedRowByLine?.[String(moveRow.line)] &&
      statusBeneficiario === MOVE_ROW_PROCESS_STATUS.WAITING_HEALTH_CARD
    ) {
      return 'Vida ativa';
    }

    return 'Editar';
  };

  const getButtonColor = (moveRow: MoveSheetNormalized): Color => {
    const statusBeneficiario = editedRowByLine?.[String(moveRow.line)]?.fields.statusBeneficiario;

    if (
      editedRowByLine?.[String(moveRow.line)] &&
      statusBeneficiario !== MOVE_ROW_PROCESS_STATUS.WAITING_HEALTH_CARD
    ) {
      return 'success';
    }

    if (
      editedRowByLine?.[String(moveRow.line)] &&
      statusBeneficiario === MOVE_ROW_PROCESS_STATUS.WAITING_HEALTH_CARD
    ) {
      return 'info';
    }

    return 'colorDefault';
  };

  const getErrors = ({ review }: MoveSheetNormalized) => {
    if (review?.errors.length === 1) {
      return review.errors[0];
    }

    return <ol>{review?.errors.map((error) => <li key={error}>{error}</li>)}</ol>;
  };

  useEffect(() => {
    if (!moveId) {
      return;
    }
    setFilters({
      fileId: moveId,
      page: MOVE_SHEET_INITIAL_PAGE,
      pageSize: MOVE_SHEET_PAGE_SIZE_DEFAUlT,
      sort: 'line',
      errorType: [MOVE_ERROR_TYPE.REVIEW_ERROR],
    });
  }, [moveId]);

  return (
    <Accordion
      full
      box
      title={
        <ReportListHeader
          errorType={MOVE_ERROR_TYPE.REVIEW_ERROR}
          total={pagination.total}
          loading={isLoading}
        />
      }>
      <DataTable
        elevation={0}
        isLoading={isLoading}
        loadingLinesNumber={linesLoader}
        data={sheet}
        columns={[
          {
            name: 'Linha',
            path: 'line',
          },
          {
            name: 'Nome',
            path: 'review.beneficiaryName',
          },
          {
            name: 'CPF',
            path: 'review.cpf',
            width: '150px',
            format: ({ review }) => review.cpf && maskText(review.cpf, '999.999.999-99'),
          },
          {
            name: 'Crítica(s)',
            path: 'review.errors',
            format: getErrors,
          },
        ]}
        page={pagination.page}
        pageSize={pagination.pageSize}
        total={pagination.total}
        totalPages={pagination.totalPages}
        onChangePage={(page) => setFilters(() => ({ ...filters, page }))}
        onChangePageSize={(pageSize) => setFilters(() => ({ ...filters, pageSize }))}
        showPagination={showPagination}
        action={{
          items: (moveRow: MoveSheetNormalized) => [
            {
              icon: ICONS.EDIT,
              label: getButtonLabel(moveRow),
              color: getButtonColor(moveRow),
              onClick: () => setRowToEdition?.(moveRow),
            },
          ],
        }}
      />
    </Accordion>
  );
};

export default ReportReviewList;
